import { useState, type FormEvent } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useSubmitFeedback } from '@/hooks/useFeedback';
import type { FeedbackType } from '@/types';

export interface FeedbackModalProps {
  show: boolean;
  onClose: () => void;
  relatedEmailId?: number;
}

const feedbackTypes: { value: FeedbackType; label: string }[] = [
  { value: 'ScoreDispute', label: 'Scoring accuracy / dispute' },
  { value: 'BugReport', label: 'Bug report' },
  { value: 'Suggestion', label: 'Feature suggestion' },
  { value: 'Other', label: 'Other' },
];

export function FeedbackModal({ show, onClose, relatedEmailId }: FeedbackModalProps) {
  const location = useLocation();
  const { mutate, isPending, isSuccess, isError, reset } = useSubmitFeedback();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('ScoreDispute');
  const [feedbackText, setFeedbackText] = useState('');

  const handleClose = () => {
    reset();
    setFeedbackText('');
    onClose();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate({
      feedbackType,
      feedbackText,
      emailId: relatedEmailId,
      contextFilters: JSON.stringify({ pathname: location.pathname }),
    });
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-chat-square-text me-2" aria-hidden="true" />
            Share Feedback
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isSuccess ? (
            <Alert variant="success" className="mb-0">
              Thanks! Your feedback was submitted and helps improve scoring quality.
            </Alert>
          ) : (
            <>
              {isError && <Alert variant="danger">Failed to submit feedback. Try again.</Alert>}
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                >
                  {feedbackTypes.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  required
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell us what's working, what's off, or what you'd like to see..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!isSuccess && (
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || !feedbackText.trim()}
            >
              {isPending ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
